<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {computed, ref} from "vue";
import {CapacityFramework, CapacityIndicator, Category} from "@/types";
import draggable from 'vuedraggable'
import VFieldLabel from "@/components/forms/VFieldLabel.vue";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import EditLabelDialog from "@/Pages/capacity_frameworks/components/EditLabelDialog.vue";
import {uuidv7} from "uuidv7";
import DuplicateBtn from "@/components/buttons/DuplicateBtn.vue";
import RemoveBtn from "@/components/buttons/RemoveBtn.vue";
import CategoryCard from "@/components/cards/CategoryCard.vue";
import RemoveDialog from "@/Pages/capacity_frameworks/components/RemoveDialog.vue";
import MoveUpBtn from "@/components/buttons/MoveUpBtn.vue";
import MoveDownBtn from "@/components/buttons/MoveDownBtn.vue";
import NavigationTabWithChip from "@/components/tabs/NavigationTabWithChip.vue";
import NavigationTab from "@/components/tabs/NavigationTab.vue";
import {useError} from "@/composables/errors";
import {useRules} from "vuetify/labs/rules";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog.vue";

defineOptions({layout: BaseLayout})

const rules = useRules()
const {hasErrors, errors} = useError()

interface Props {
    title: string
    category: 'overview' | 'form'
    capacityFramework: CapacityFramework
    structure: Array<Category>
}

const props = defineProps<Props>();

const form = ref<HTMLFormElement>();

const tab = ref<'overview' | 'form'>(props.category);
const isConfirmDialogOpen = ref<boolean>(false);
const items = ref<Array<Category>>(props.structure);

const isReadOnly = computed(() => {
    return props.capacityFramework.status === 'published';
});

const goToOverview = () => {
    router.get(route('capacity-frameworks.edit', {capacityFramework: props.capacityFramework.id, version: props.capacityFramework.version_id}));
}

const addCategory = () => {
    const newCategory: Category = {
        id: uuidv7(),
        title: 'New Category',
        order: items.value.length,
        capacity_indicators: []
    }
    items.value.push(newCategory);
    updateCategoryOrders(items.value);
}

const moveCategoryUp = (category: Category) => {
    const index = items.value.findIndex(item => item.id === category.id);
    if (index > 0) {
        const temp = items.value[index - 1];
        items.value[index - 1] = items.value[index];
        items.value[index] = temp;
        updateCategoryOrders(items.value);
    }
}

const moveCategoryDown = (category: Category) => {
    const index = items.value.findIndex(item => item.id === category.id);
    if (index < items.value.length - 1) {
        const temp = items.value[index + 1];
        items.value[index + 1] = items.value[index];
        items.value[index] = temp;
        updateCategoryOrders(items.value);
    }
}

const removeCategory = (category: Category) => {
    items.value = items.value.filter(item => item.id !== category.id);
    updateCategoryOrders(items.value);
}

const updateCategoryOrders = (list: Array<{ order: number }>) => {
    list.forEach((item, index) => {
        item.order = index
    });
}

const duplicateIndicator = (indicator: CapacityIndicator, category: Category) => {
    const id = uuidv7();
    const newIndicator = {
        ...indicator,
        id: id,
        order: indicator.order + 1,
        category_id: category.id,
        options: indicator.options.map(option => ({...option, id: uuidv7(), capacity_indicator_id: id}))
    };
    category.capacity_indicators.splice(indicator.order + 1, 0, newIndicator);
    updateIndicatorOrders(category.id, category.capacity_indicators);
}

const addIndicator = (category: Category) => {
    const id = uuidv7();
    const newIndicator: CapacityIndicator = {
        id: id,
        title: 'New Indicator',
        description: '',
        order: category.capacity_indicators.length,
        category_id: category.id,
        options: [
            {id: uuidv7(), capacity_indicator_id: id, value: '', order: 0},
            {id: uuidv7(), capacity_indicator_id: id, value: '', order: 1}
        ]
    }
    category.capacity_indicators.push(newIndicator);
    updateIndicatorOrders(category.id, category.capacity_indicators);
}

const moveIndicatorUp = (indicator: CapacityIndicator, category: Category) => {
    const index = category.capacity_indicators.findIndex(ind => ind.id === indicator.id);
    if (index > 0) {
        const temp = category.capacity_indicators[index - 1];
        category.capacity_indicators[index - 1] = category.capacity_indicators[index];
        category.capacity_indicators[index] = temp;
        updateIndicatorOrders(category.id, category.capacity_indicators);
    }
}

const moveIndicatorDown = (indicator: CapacityIndicator, category: Category) => {
    const index = category.capacity_indicators.findIndex(ind => ind.id === indicator.id);
    if (index < category.capacity_indicators.length - 1) {
        const temp = category.capacity_indicators[index + 1];
        category.capacity_indicators[index + 1] = category.capacity_indicators[index];
        category.capacity_indicators[index] = temp;
        updateIndicatorOrders(category.id, category.capacity_indicators);
    }
}

const removeIndicator = (indicator: CapacityIndicator, category: Category) => {
    category.capacity_indicators = category.capacity_indicators.filter(ind => ind.id !== indicator.id);
    updateIndicatorOrders(category.id, category.capacity_indicators);
}

const updateIndicatorOrders = (categoryId: string, indicators: Array<CapacityIndicator>) => {
    indicators.forEach((indicator, index) => {
        indicator.category_id = categoryId;
        indicator.order = index
    });
}

const updateOptionsOrders = (indicator: CapacityIndicator, options: Array<{ order: number }>) => {
    options.forEach((option, index) => {
        option.order = index
    });
}

const addOption = (indicator: CapacityIndicator) => {
    const newOption = {
        id: uuidv7(),
        capacity_indicator_id: indicator.id,
        value: '',
        order: indicator.options.length
    }
    indicator.options.push(newOption);
    updateOptionsOrders(indicator, indicator.options);
}

const removeOption = (indicator: CapacityIndicator, option: { id: string }) => {
    indicator.options = indicator.options.filter(opt => opt.id !== option.id);
    updateOptionsOrders(indicator, indicator.options);
}

const update = async () => {
    router.put(route('capacity-frameworks.update-form', {capacityFramework: props.capacityFramework.id, version: props.capacityFramework.version_id}), {
        form: items.value
    }, {
        preserveState: true,
        preserveScroll: true,
    })
}

const publish = async () => {
    isConfirmDialogOpen.value = true;
}

const updateAndPublish = async () => {
    router.put(route('capacity-frameworks.update-form', {capacityFramework: props.capacityFramework.id, version: props.capacityFramework.version_id, _query: { publish: true }}), {
        form: items.value
    }, {
        preserveState: true,
        preserveScroll: true,
    })
}

const submit = async () => {
    const {valid} = await form.value?.validate()

    if (!valid) {
        return;
    }

    if (props.capacityFramework.status === 'draft') {
        await publish();
    } else {
        await update();
    }
}

const createNewVersion = () => {
    router.post(route('capacity-frameworks.version', {capacityFramework: props.capacityFramework.id, version: props.capacityFramework.version_id }))
}
</script>

<template>
    <v-container>
        <v-row class="align-center justify-space-between">
            <v-col
                cols="12"
                class="pa-0"
            >
                <h1 class="text-h4 font-weight-bold">
                    {{ props.title }} <span class="text-disabled">v{{ props.capacityFramework.version_id }}</span>
                </h1>
            </v-col>
        </v-row>
        <v-row class="mt-5 mb-0">
            <v-col
                cols="12"
                class="pa-0"
            >
                <v-tabs
                    v-model="tab"
                    align-tabs="start"
                    color="primary"
                >
                    <navigation-tab
                        :tab="tab"
                        value="overview"
                        @click="goToOverview"
                    >
                        Overview
                    </navigation-tab>
                    <navigation-tab-with-chip
                        :tab="tab"
                        value="form"
                        :count="props.capacityFramework.capacity_indicators_count"
                    >
                        Form
                    </navigation-tab-with-chip>
                </v-tabs>
            </v-col>
        </v-row>
        <v-divider />
        <v-form
            ref="form"
            @submit.prevent="submit"
        >
            <v-row>
                <v-col
                    cols="12"
                >
                    <v-alert
                        v-if="props.capacityFramework.status === 'draft'"
                        variant="tonal"
                        type="info"
                        color="primary"
                        class="ma-3"
                        closable
                    >
                        <span class="text-body-2 text-black">
                            This version of the framework is currently being drafted and is not yet published. Be careful before publishing it, as once published, its structure (categories, indicators and order) cannot be edited anymore.
                        </span>
                    </v-alert>
                    <v-alert
                        v-if="props.capacityFramework.status === 'published'"
                        variant="tonal"
                        type="info"
                        color="primary"
                        class="ma-3"
                        closable
                    >
                        <p class="text-body-2 text-black">
                            This version of the framework is published and its structure cannot be edited. You
                            can still edit the labels and values. If you want to make changes to the structure, you can
                            create a new version.
                        </p>
                        <v-btn
                            v-if="props.capacityFramework.version_id === props.capacityFramework.latest_version"
                            color="primary"
                            class="mt-2"
                            @click="createNewVersion"
                        >
                            Create a new version
                        </v-btn>
                    </v-alert>

                    <v-sheet>
                        <category-card
                            v-for="item in items"
                            :key="item.id"
                        >
                            <template #title>
                                <span class="text-disabled">#{{ item.order + 1 }}</span> {{ item.title }}
                                <edit-label-dialog
                                    v-model:label="item.title"
                                />
                                <move-up-btn
                                    v-if="item.order > 0"
                                    :disabled="isReadOnly"
                                    @click="moveCategoryUp(item)"
                                />
                                <move-down-btn
                                    v-if="item.order < items.length - 1"
                                    :disabled="isReadOnly"
                                    @click="moveCategoryDown(item)"
                                />
                                <remove-dialog
                                    v-if="items.length > 1"
                                    :disabled="isReadOnly"
                                    @submit="removeCategory(item)"
                                />
                            </template>
                            <template #default>
                                <v-card
                                    v-for="indicator in item.capacity_indicators"
                                    :key="indicator.id"
                                    color="#FAF9FC"
                                    border="sm"
                                    variant="flat"
                                    class="ma-3"
                                >
                                    <v-card-title
                                        class="text-body-2 bg-card-custom border-b-sm"
                                    >
                                        <span class="text-disabled">#{{ indicator.order + 1 }}</span> {{
                                            indicator.title
                                        }}
                                        <edit-label-dialog
                                            v-model:label="indicator.title"
                                        />
                                        <move-up-btn
                                            v-if="indicator.order > 0"
                                            :disabled="isReadOnly"
                                            @click="moveIndicatorUp(indicator, item)"
                                        />
                                        <move-down-btn
                                            v-if="indicator.order < item.capacity_indicators.length - 1"
                                            :disabled="isReadOnly"
                                            @click="moveIndicatorDown(indicator, item)"
                                        />
                                        <duplicate-btn
                                            :disabled="isReadOnly"
                                            @click="duplicateIndicator(indicator, item)"
                                        />
                                        <remove-btn
                                            class="float-end"
                                            :disabled="isReadOnly"
                                            @click="removeIndicator(indicator, item)"
                                        />
                                    </v-card-title>
                                    <v-card-text class="pa-3 bg-white">
                                        <v-field-label>
                                            Description (optional)
                                        </v-field-label>
                                        <v-text-field
                                            v-model="indicator.description"
                                            density="compact"
                                            class="mb-3"
                                            hide-details
                                        />
                                        <v-field-label>
                                            Answers
                                        </v-field-label>
                                        <draggable
                                            :disabled="isReadOnly"
                                            :list="indicator.options"
                                            item-key="id"
                                            tag="VList"
                                            group="options"
                                            :component-data="{
                                                class: 'pa-0 bg-white',
                                            }"
                                            handle=".cursor-grab"
                                            @update="updateOptionsOrders(indicator, indicator.options)"
                                        >
                                            <template #[`item`]="{ element: option }">
                                                <v-list-item
                                                    class="pa-2"
                                                >
                                                    <template #prepend>
                                                        <v-icon
                                                            v-if="!isReadOnly"
                                                            class="cursor-grab mt-1"
                                                            size="24"
                                                        >
                                                            mdi-drag
                                                        </v-icon>
                                                    </template>
                                                    <v-field-label>
                                                        Answer {{ option.order + 1 }}
                                                        <remove-btn
                                                            class="float-end"
                                                            :disabled="isReadOnly"
                                                            @click="removeOption(indicator, option)"
                                                        />
                                                    </v-field-label>
                                                    <v-textarea
                                                        v-model="option.value"
                                                        density="compact"
                                                        :placeholder="`Answer ${option.order + 1}`"
                                                        rows="1"
                                                        auto-grow
                                                        :rules="[rules.required(), rules.pattern(/^(?!\s*$).+/)]"
                                                    />
                                                </v-list-item>
                                            </template>
                                        </draggable>
                                        <v-row>
                                            <v-col
                                                cols="12"
                                                class="pl-15 pr-5 pt-5"
                                            >
                                                <v-btn
                                                    v-if="!isReadOnly"
                                                    color="#FAF9FC"
                                                    border="sm"
                                                    block
                                                    @click="addOption(indicator)"
                                                >
                                                    Add a new answer option
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                                <v-row>
                                    <v-col
                                        cols="12"
                                        class="px-6"
                                    >
                                        <v-btn
                                            v-if="!isReadOnly"
                                            color="#FAF9FC"
                                            border="sm"
                                            block
                                            @click="addIndicator(item)"
                                        >
                                            Add a new capacity indicator
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </template>
                        </category-card>
                        <v-btn
                            v-if="!isReadOnly"
                            color="#FAF9FC"
                            border="sm"
                            class="mt-3"
                            block
                            @click="addCategory"
                        >
                            Add new category
                        </v-btn>
                    </v-sheet>
                </v-col>
            </v-row>

            <v-row
                v-if="hasErrors"
                class="mt-5"
            >
                <v-alert
                    variant="tonal"
                    type="error"
                    :icon="false"
                    class="text-center text-body-2 mt-5"
                >
                    {{ errors.form }}
                </v-alert>
            </v-row>

            <v-row class="mt-5">
                <v-col
                    cols="12"
                    class="px-0"
                >
                    <v-btn
                        color="primary"
                        type="submit"
                    >
                        {{ props.capacityFramework.status === 'draft' ? 'Save changes and publish' : 'Save changes' }}
                    </v-btn>
                    <v-btn
                        v-if="props.capacityFramework.status === 'draft'"
                        color="black"
                        variant="text"
                        border="sm"
                        class="ml-2"
                        @click="update"
                    >
                        <span class="font-weight-regular">
                            Save changes
                        </span>
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
    <confirm-dialog
        v-model="isConfirmDialogOpen"
        @submit="updateAndPublish"
    >
        <template #title>
            Are you sure you want to publish this version of the framework?
        </template>
        <template #default>
            <v-alert
                variant="tonal"
                type="info"
                color="primary"
                class="text-body-2"
            >
                Once published, the structure of this version (categories, indicators and order) cannot be edited anymore. You can still edit the labels and values.
            </v-alert>
        </template>
        <template #label>
            Yes, publish version
        </template>
    </confirm-dialog>
</template>

<style scoped>
.cursor-grab {
    cursor: all-scroll !important;
}

:deep(.v-list-item__prepend) {
    align-items: start;
    align-self: start;
    width: 42px;
}
</style>
